/**
 * Banner top-fijo que avisa cuando el backend está caído o el usuario
 * perdió internet. Es global (no per-page) y consume `BackendStatusContext`.
 *
 * Coexiste con `<OfflineBanner>`: el OfflineBanner solo cubre el caso
 * `navigator.onLine === false` con un mensaje inmediato. Este banner
 * extiende esa cobertura a errores 5xx del backend (server caído, deploys
 * con downtime, etc.) y agrega el botón "Reintentar".
 *
 * No bloquea la UI — el usuario sigue viendo lo que tenía cargado y
 * puede seguir trabajando con esa info. La intención es que sea claro
 * que hay un problema sin secuestrar la pantalla.
 */

import { useBackendStatus } from '@/contexts/BackendStatusContext';

export function BackendStatusBanner() {
  const { status, retry, isRetrying } = useBackendStatus();

  if (status === 'ok') return null;

  const copy =
    status === 'network-error'
      ? {
          title: 'Sin conexión',
          description:
            'No podemos conectarnos al servidor. Revisa tu internet.',
        }
      : {
          title: 'Servidor no disponible',
          description:
            'Estamos teniendo problemas para responder. Inténtalo en un momento.',
        };

  return (
    <div
      role="alert"
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-3 bg-error px-4 py-2 text-center text-sm font-medium text-error-foreground shadow-md"
    >
      {/* SVG, no emoji — regla del design system (íconos solo en SVG). */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className="h-4 w-4 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span className="flex flex-col items-start sm:flex-row sm:items-center sm:gap-2">
        <strong>{copy.title}</strong>
        <span className="font-normal opacity-90">{copy.description}</span>
      </span>
      <button
        type="button"
        onClick={retry}
        disabled={isRetrying}
        className="ml-2 cursor-pointer rounded-lg bg-error-foreground/15 px-3 py-1 text-xs font-medium hover:bg-error-foreground/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-foreground/60 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isRetrying ? 'Probando…' : 'Reintentar'}
      </button>
    </div>
  );
}
